import React, {Dispatch, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ExternalAssetAlbum,
  ExternalAssetResponse,
  RemoteAssetServiceProps,
} from '../RemoteAssetService';
import useHistory from '../../../Hooks/AssetService/useHistory';
import {Button, PictureIcon, SearchIcon, Theme} from '@taopix/taopix-design-system';
import {ExternalAssetMeta} from '../../../Utils/Asset';
import {PictureSortType} from '../AddPicturesButton';
import useExternalServiceAPI from '../../../Hooks/AssetService/useExternalServiceAPI';
import AssetServiceNavBar from '../Nav/AssetServiceNavBar';
import PricingKey from './PricingKey';
import Authentication from './Authentication';
import {AssetServiceActions} from '@taopix/taopix-core';
import MediaGallery from '../../MediaGallery/MediaGallery';
import {useDispatch} from 'react-redux';
import {ActionType} from 'typesafe-actions';
import useSettings from '../../../Hooks/useSettings';
import {useAssetServices} from '../../Context/AssetServices/AssetServicesContext';
import {AssetServicesActions} from '../../Context/AssetServices/AssetServicesActions';

const LibraryLink = ({
  isOpen,
  closeEvent,
  assetService,
  designerSettings,
  server,
  projectImageList,
  ...props
}: RemoteAssetServiceProps) => {
  const settings = useSettings();
  const services = useAssetServices();
  const {t} = useTranslation(['picturemanager']);

  const [selectedAlbumsPhotos, setSelectedAlbumPhotos] = useState<Partial<ExternalAssetMeta>[]>([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState<string>('');
  const [nextPageToken, setNextPageToken] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [lock, setLock] = useState<boolean>(false);
  const [searchMode, setSearchMode] = useState<boolean>(false);
  const [history, setHistory, back] = useHistory<ExternalAssetAlbum>();
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const api = useExternalServiceAPI(server);

  // create the action dispatcher for the asset service
  const dispatch = useDispatch<Dispatch<ActionType<typeof AssetServiceActions>>>();

  useEffect(() => {
    // set the initial album, which is all images
    setHistory([
      {
        id: 'me',
        imgSrc: <PictureIcon className="h-42 w-auto block m-auto" />,
        name: t('picturemanager:str_ButtonAllPhotos'),
        onSelectAlbum: getPhotosFromAlbum,
        immutable: true,
      },
    ]);

    if (assetService.searchmode === 1) {
      setSearchMode(true);
    }

    if (assetService.accesstoken !== '') {
      // clear any errors
      setError('');

      setNextPageToken('');
      setSelectedAlbumId('me');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetService.accesstoken]);

  // when the selected album changes, get the photos from that album
  useEffect(() => {
    if (error.length) {
      setError('');
    }

    if (selectedAlbumId === '') {
      return;
    }

    setNextPageToken('');
    // reset the current photos
    setSelectedAlbumPhotos([]);
    getPhotosFromAlbum(selectedAlbumId, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlbumId, searchTerm]);

  /**
   * Navigate the history back a number of levels
   */
  const onNavigate = (levels = 1) => {
    if (0 === levels) {
      return;
    }
    let lastAlbum = back(levels);
    if (null === lastAlbum) {
      lastAlbum = {
        id: 'me',
        imgSrc: <PictureIcon className="h-42 w-auto block m-auto" />,
        name: t('picturemanager:str_ButtonAllPhotos'),
        onSelectAlbum: getPhotosFromAlbum,
        immutable: true,
      };
      // setHistory([lastAlbum]);
    }

    setNextPageToken('');
    setSelectedAlbumPhotos([]);
    setSearchTerm('');
    setSelectedAlbumId(lastAlbum.id);
  };

  /**
   * Set the access token and authenticated state
   *
   * @param authenticated
   * @param accessToken
   */
  const onAuthenticate = (authenticated: boolean, accessToken: string) => {
    dispatch(
      AssetServiceActions.updateAssetService({
        ...assetService,
        accesstoken: accessToken,
      })
    );
  };

  /**
   * Clear the access token and authenticated state
   */
  const onLogout = () => {
    dispatch(
      AssetServiceActions.updateAssetService({
        ...assetService,
        accesstoken: '',
      })
    );
  };

  /**
   *
   *
   * @param payload
   * @param albumId
   * @param back
   * @param llAlbum
   * @param searchMode
   */
  const onSetPhotos = (payload: ExternalAssetResponse, albumId: string, searchTerm = '') => {
    const {accesstoken, code} = assetService;
    // the assets are localised so run the names through the locale
    const assets = payload.data.photos.map(a => {
      return {
        ...a,
        accessToken: accesstoken,
        serviceCode: code,
      };
    });

    // if we have a next page token, then set it
    if (nextPageToken !== payload.data.nextPageToken) {
      setNextPageToken(payload.data.nextPageToken);
    }

    if (albumId === selectedAlbumId && '' === searchTerm) {
      setSelectedAlbumPhotos(current => {
        return [...current, ...assets];
      });
    } else {
      setSelectedAlbumPhotos(assets);
    }

    setLock(false);
  };

  const getPhotosFromAlbum = (albumId: string, searchTerm = '') => {
    setLoading(true);

    api
      .getPhotos({
        assetservice: JSON.stringify(assetService),
        accessToken: assetService.accesstoken,
        folderId: albumId,
        nextPageToken: albumId !== selectedAlbumId || false || searchMode ? '' : nextPageToken,
        groupCode: designerSettings.keySettings.groupCode,
        libraryLinkPath: albumId,
        libraryLinkSearchTerm: searchTerm,
        serviceCode: assetService.code.replace(/%/g, ''),
      })
      .then(response => {
        if (response.error.code) {
          onError(response.error.fullMessage);
        } else {
          onSetPhotos(response, albumId, searchTerm);
        }
        setLoading(false);
      })
      .catch(error => {
        onError(t('assetservices:str_ErrorAssetServiceAuthentication'));
        setLoading(false);
        onLogout();
      });
  };

  const onClickAlbum = (id: string, path: string) => {
    if (!lock) {
      setLock(true);
      const asset = selectedAlbumsPhotos.find(photo => String(photo.id) === String(id));
      if (!asset) {
        return setError('Asset not found');
      }

      const historyStack: ExternalAssetAlbum[] = [
        ...history,
        {
          id: path,
          imgSrc: String(asset.src),
          name: String(asset.name),
          onSelectAlbum: () => {},
        },
      ];

      // set the new history stack
      setHistory(historyStack);

      // reset the next page token
      setNextPageToken('');

      // build the path from the historyStack filtering out the entry with id === 'me'
      const newPath = historyStack
        .filter(h => h.id !== 'me')
        .map(h => h.id)
        .join('/')
        .replace(/\/\//g, '/');

      // set the selected album id
      setSelectedAlbumId(newPath);
    }
  };

  const onError = (error: any) => {
    setError(error);
  };

  const nextPagePhotos = () => {
    if (nextPageToken !== '') {
      getPhotosFromAlbum(selectedAlbumId);
    } else {
      console.error('no next page token');
    }
  };

  const onSelectAsset = (checked: boolean, asset: any) => {
    const assets = new Set([...services.state.assets]);
    if (checked) {
      if (!assets.has(asset)) {
        assets.add(asset);
      }
    } else {
      if (assets.has(asset)) {
        assets.delete(asset);
      }
    }
    services.dispatch(AssetServicesActions.setSelectedAssets(Array.from(assets)));
  };

  const onSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    setSelectedAlbumId('search');

    setHistory(current => {
      if (!current.find(entry => entry.id === 'search')) {
        return [
          ...current,
          {
            id: 'search',
            imgSrc: <SearchIcon className="h-42 w-auto block m-auto" />,
            name: t('assetservices:str_LabelSearch'),
            onSelectAlbum: getPhotosFromAlbum,
            immutable: true,
          },
        ];
      }
      return current;
    });
  };

  // if we are not authenticated, show the auth view
  if (assetService.accesstoken === '') {
    return (
      <Theme
        name={'contained'}
        className={'flex flex-auto flex-col justify-center items-center'}
        allowCorners={false}
      >
        {/* <Error error={error} /> */}
        <Authentication
          server={server}
          designerSettings={designerSettings}
          isOpen={false}
          closeEvent={() => {}}
          assetService={assetService}
          onAuthenticate={onAuthenticate}
        />
      </Theme>
    );
  }

  return (
    <>
      <AssetServiceNavBar
        searchMode={searchMode}
        searchFunction={onSearch}
        authenticated={assetService.accesstoken !== ''}
        currentAlbumId={selectedAlbumId.split('/').pop() || ''}
        assetService={assetService}
        goHome={() => {}}
        goBack={onNavigate}
        goUserProfile={() => {}}
        albums={history}
        onClickAlbum={getPhotosFromAlbum}
        assetServiceLogout={onLogout}
      />

      {assetService.showpricekey && <PricingKey />}

      <div className={'overflow-auto'}>
        <MediaGallery
          selectedImages={services.state.assets.map(a => a.id)}
          disabledAssetIds={
            (projectImageList
              .map(asset => {
                if (asset.externalId !== '') {
                  return asset.externalId;
                }
                return null;
              })
              .filter(a => a) as string[]) || []
          }
          groupedView={false}
          selectableImages={true}
          onSelectPicture={onSelectAsset}
          hideUsed={false}
          thumbnailLayout={'full-frame-tiles'}
          sortBy={PictureSortType.Filename}
          sortDirection={'asc'}
          imageList={selectedAlbumsPhotos as any}
          pictureUsage={{}}
          onSelectAlbum={onClickAlbum}
          isExternalService
          showPricing
          currencyCode={settings.state.currency.code}
        />

        {'' !== nextPageToken && (
          <div className={'flex flex-1 justify-center items-center'}>
            <Button
              buttonStyle={!loading ? 'distinct' : 'tertiary'}
              className={'self-center'}
              label={!loading ? t('helptips:str_ButtonLoadMore') : t('*:str_LabelLoadingEllipsis')}
              disabled={loading}
              loading={loading}
              onClick={nextPagePhotos}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default LibraryLink;
